import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { useAccountStore } from "@/_store/account.module";
import { socAddonsEnabled } from "@/_helpers/subscription";
import { isWorkspaceInitial } from "@/_helpers/subscription";
import {
  type GlobalRolePermissions,
  type MspPortalPermissions,
  type NestedGlobalPermissions,
  type WorkspaceRolePermissions,
} from "@/_store/roles.module";
import { SubscriptionAddon, SubscriptionModule } from "@/constants/workplaces";
import { CLOUD_BACKUP_WORKSPACE_TYPES } from "@/constants/cloud-backup";
import { isMspAccessRestricted } from "@/_helpers/msp-permissions";
import { isGlobalAccessRestricted, isGlobalActionRestricted } from "@/_helpers/global-permissions";
import { isAddonDisabled, isModuleDisabled, isWorkspaceFrozen } from "@/_helpers/subscription";
import { RouteName } from "@/constants/routes";
import { useMyAccountStore } from "@/_store/my-account.module";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useNetworkAnalyticsStore } from "@/_store/network/network-analytics.module.ts";
import { isAccessRestricted } from "@/_helpers/permissions.ts";
import {
  GlobalRoleScopes,
  GlobalRoleScopeSection,
  MspPortalScopeSection,
  MspRolePermissionScopes,
  RolePermissionsScope,
  WorkspaceManagementScopeSections,
} from "@/constants/roles.ts";

/**
 * Navigation guard for authentication.
 *
 */
export const authGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const accountStore = useAccountStore();
  const myAccountStore = useMyAccountStore();
  const workspaceStore = useWorkspaceStore();
  const { logged } = accountStore;
  const workspace = workspaceStore.workspace.workspaceId;
  const authRequired = !to.meta.publicPage;
  const alwaysPublicPage = to.meta.alwaysPublicPage;

  // Allow navigation to pages that are always public
  if (alwaysPublicPage) {
    return next();
  }

  // if logged in on invited page, we should forward to workspaces page where user can accept invite
  if (to.name === RouteName.INVITED_PAGE) {
    if (logged) {
      workspaceStore.$patch((state) => {
        state.workspace.workspaceId = "";
      });
      return next({ name: RouteName.WORKSPACES });
    }
    return next();
  }

  // mfa setup pages should be accessible only for certain admins
  if (to.name === RouteName.MFA_PAGE) {
    if (!myAccountStore.myAccount.profileData.allowMfa) {
      return next({ name: RouteName.DASHBOARD_PAGE });
    }
    return next();
  }

  // Redirect to login if the page requires authentication and the user is not logged in
  if (authRequired && !logged) {
    return next({ name: RouteName.LOGIN });
  }

  // Redirect to the dashboard if accessing a public page while logged in
  if (!authRequired && logged) {
    return next({ name: RouteName.DASHBOARD_PAGE });
  }
  // Redirect to workplace selection if no workplace is set and accessing any page other than workspaces
  if (!workspace && logged && to.name !== RouteName.WORKSPACES) {
    return next({ name: RouteName.WORKSPACES });
  }
};

/**
 * Redirects when the given subscription module is disabled.
 * @param moduleName
 * @param redirectRouteName
 */
export const moduleNavigationGuard = (
  moduleName: SubscriptionModule,
  redirectRouteName: RouteName = RouteName.DASHBOARD_PAGE
) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    return isModuleDisabled(moduleName) ? next({ name: redirectRouteName }) : next();
  };
};

/**
 * Redirects when the current workspace is frozen.
 * @param redirectRouteName
 */
export const workspaceFrozenGuard = (redirectRouteName: RouteName) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    if (isWorkspaceFrozen()) {
      return next({ name: redirectRouteName });
    }
    return next();
  };
};

/**
 * Redirects when the current workspace is in its initial (not yet activated) state.
 * @param redirectRouteName
 */
export const workspaceInitialGuard = (redirectRouteName: RouteName) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    if (isWorkspaceInitial()) {
      return next({ name: redirectRouteName });
    }
    return next();
  };
};

/**
 * Redirects when the current workspace cannot have Cloud Backup of its own — see
 * CLOUD_BACKUP_WORKSPACE_TYPES.
 * @param redirectRouteName
 */
export const cloudBackupWorkspaceGuard = (
  redirectRouteName: RouteName = RouteName.DASHBOARD_PAGE
) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const workspaceStore = useWorkspaceStore();
    return CLOUD_BACKUP_WORKSPACE_TYPES.includes(workspaceStore.workspace.type)
      ? next()
      : next({ name: redirectRouteName });
  };
};

/**
 * Redirects to the dashboard when the given subscription addon is disabled.
 * @param addonName
 */
export const addonNavigationGuard = (addonName: SubscriptionAddon) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    return isAddonDisabled(addonName) ? next({ name: RouteName.DASHBOARD_PAGE }) : next();
  };
};

/**
 * Allows access only when the user is logged in and SOC addons are enabled.
 * @param to
 * @param from
 * @param next
 */
export const socGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const accountStore = useAccountStore();
  const { logged } = accountStore;
  return logged && socAddonsEnabled() ? next() : next({ name: RouteName.DASHBOARD_PAGE });
};

/**
 * Redirects when SOC portal access is restricted at the global scope.
 * @param to
 * @param from
 * @param next
 */
export const socTicketsLogGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const socTicketsLogDisabled = isGlobalAccessRestricted(
    GlobalRoleScopes.GLOBAL_SCOPE,
    GlobalRoleScopeSection.SOC_PORTAL
  );
  return socTicketsLogDisabled ? next({ name: RouteName.DASHBOARD_PAGE }) : next();
};

/**
 * Redirects when access to the MSP global workspaces section is restricted.
 * @param to
 * @param from
 * @param next
 */
export const globalWorkspacesGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const globalWorkspacessAccessRestricted = isGlobalAccessRestricted(
    MspRolePermissionScopes.MSP_PORTAL_SCOPE,
    MspPortalScopeSection.MSP_WORKSPACES
  );
  return globalWorkspacessAccessRestricted ? next({ name: RouteName.DASHBOARD_PAGE }) : next();
};

/**
 * Redirects when the workspace role lacks access to the given scope/key.
 * @param scopeName
 * @param key
 */
export const permissionsGuard = <
  T extends RolePermissionsScope = RolePermissionsScope,
  K extends keyof WorkspaceRolePermissions[T] =
    keyof WorkspaceRolePermissions[RolePermissionsScope],
>(
  scopeName: T,
  key: K
) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const accessRestricted = isAccessRestricted(scopeName, key);
    return accessRestricted ? next({ name: RouteName.DASHBOARD_PAGE }) : next();
  };
};

type AccessControlPageSection =
  | WorkspaceManagementScopeSections.ADMIN_USERS
  | WorkspaceManagementScopeSections.ROLES
  | WorkspaceManagementScopeSections.ACTIVE_SESSIONS;

type TabGuardParams<T extends string> = {
  accessRestricted: (key: T) => boolean;
  tabs: readonly T[];
  tabNameToRouteName: Record<T, string>;
};

/**
 * Redirects to the MSP dashboard when the MSP role lacks access to the given scope/key.
 * @param scopeName
 * @param key
 */
export const mspPagePermissionGuard = <
  T extends keyof MspPortalPermissions = keyof MspPortalPermissions,
  K extends keyof MspPortalPermissions[T] = keyof MspPortalPermissions[T],
>(
  scopeName: T,
  key: K
) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const accessRestricted = isMspAccessRestricted(scopeName, key);
    return accessRestricted ? next({ name: RouteName.MSP_DASHBOARD_PAGE }) : next();
  };
};

/**
 * Redirects to outbound gateway settings when inbound gateway addon is disabled.
 * @param to
 * @param from
 * @param next
 */
export const gatewaySettingsGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  if (
    to.name === RouteName.GATEWAY_SETTINGS_INBOUND &&
    isAddonDisabled(SubscriptionAddon.INBOUND_GATEWAY)
  ) {
    next({ name: RouteName.GATEWAY_SETTINGS_OUTBOUND });

    return;
  }

  next();
};

/**
 * Redirects away from the Network Analytics board when this environment does not serve it.
 *
 * The flags are read from the backend, so this has to be async — which is also why the tab is the network
 * page's default target rather than being chosen by a `redirect`: route-level redirects are resolved
 * before any guard runs and cannot await anything. Landing on the board and bouncing off it is what lets
 * Virtual Office remain the effective landing tab when the board is switched off.
 * @param to
 * @param from
 * @param next
 */
export const networkAnalyticsGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  try {
    const { boardVisible } = await useNetworkAnalyticsStore().getFeatures();

    return boardVisible ? next() : next({ name: RouteName.NETWORK_VPN_TAB });
  } catch (e) {
    // next() has to be called on every path: a guard that neither resolves nor rejects leaves the
    // navigation unfinished, and the click on the Network tile silently does nothing at all. The store
    // already swallows request failures, so reaching here means something unforeseen - fall through to
    // Virtual Office, which is where a hidden board sends the user anyway.
    console.error(e);

    return next({ name: RouteName.NETWORK_VPN_TAB });
  }
};

/**
 * Redirects to settings when both Network and SWG modules are disabled.
 * @param to
 * @param from
 * @param next
 */
export const networkSettingsGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  if (isModuleDisabled(SubscriptionModule.NETWORK) && isModuleDisabled(SubscriptionModule.SWG)) {
    next({ name: RouteName.SETTINGS });

    return;
  }

  next();
};

/**
 * Factory that captures a tab list + `accessRestricted` predicate + tab-to-route map and returns
 * a `(tab) => NavigationGuard` builder. The produced guard redirects to `fallbackRouteName` when
 * every tab is restricted, redirects to the first accessible tab's route when the requested `tab`
 * is restricted, and otherwise calls `next()`.
 * @param params
 * @param fallbackRouteName
 */
const createPageTabGuard = <T extends string>(
  { accessRestricted, tabs, tabNameToRouteName }: TabGuardParams<T>,
  fallbackRouteName: RouteName = RouteName.DASHBOARD_PAGE
) => {
  return (tab: T) => {
    return (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      const allTabsRestricted = tabs.every((t) => accessRestricted(t));

      if (allTabsRestricted) {
        return next({ name: fallbackRouteName });
      }

      const firstAvailableTab = tabs.find((t) => !accessRestricted(t))!;

      if (accessRestricted(tab)) {
        return next({ name: tabNameToRouteName[firstAvailableTab] });
      }

      return next();
    };
  };
};

/**
 * Allows access only when the account has MSP portal access.
 * @param to
 * @param from
 * @param next
 */
export const mspPortalGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const accountStore = useAccountStore();
  const { hasMspAccess } = accountStore;
  return hasMspAccess ? next() : next({ name: RouteName.DASHBOARD_PAGE });
};

/**
 * Tab guard for the workspace access control page (admin users, roles, active sessions).
 */
export const permissionsPageTabGuard = createPageTabGuard({
  accessRestricted: (key: AccessControlPageSection) =>
    isAccessRestricted(RolePermissionsScope.WORKSPACE_MANAGEMENT, key),
  tabs: [
    WorkspaceManagementScopeSections.ADMIN_USERS,
    WorkspaceManagementScopeSections.ROLES,
    WorkspaceManagementScopeSections.ACTIVE_SESSIONS,
  ],
  tabNameToRouteName: {
    [WorkspaceManagementScopeSections.ADMIN_USERS]: RouteName.ACCESS_CONTROL_ADMIN_USERS_TAB,
    [WorkspaceManagementScopeSections.ROLES]: RouteName.ACCESS_CONTROL_ROLES_TAB,
    [WorkspaceManagementScopeSections.ACTIVE_SESSIONS]:
      RouteName.ACCESS_CONTROL_ACTIVE_SESSIONS_TAB,
  },
});

/**
 * Tab guard for the MSP access control page (MSP admin users, MSP roles).
 */
export const mspAccessControlPageTabGuard = createPageTabGuard(
  {
    accessRestricted: (
      key: MspPortalScopeSection.MSP_ADMIN_USERS | MspPortalScopeSection.MSP_ROLES
    ) => isMspAccessRestricted(MspRolePermissionScopes.MSP_PORTAL_SCOPE, key),
    tabs: [MspPortalScopeSection.MSP_ADMIN_USERS, MspPortalScopeSection.MSP_ROLES],
    tabNameToRouteName: {
      [MspPortalScopeSection.MSP_ADMIN_USERS]: RouteName.MSP_ADMIN_USERS_TAB,
      [MspPortalScopeSection.MSP_ROLES]: RouteName.MSP_ROLES_TAB,
    },
  },
  RouteName.MSP_DASHBOARD_PAGE
);

/**
 * Tab guard for the global permissions page (global admin users, global roles).
 */
export const globalPermissionsPageTabGuard = createPageTabGuard({
  accessRestricted: (
    key: GlobalRoleScopeSection.GLOBAL_ROLES | GlobalRoleScopeSection.GLOBAL_ADMIN_USERS
  ) => isGlobalAccessRestricted(GlobalRoleScopes.GLOBAL_SCOPE, key),
  tabs: [GlobalRoleScopeSection.GLOBAL_ADMIN_USERS, GlobalRoleScopeSection.GLOBAL_ROLES],
  tabNameToRouteName: {
    [GlobalRoleScopeSection.GLOBAL_ADMIN_USERS]: RouteName.GLOBAL_ADMIN_USERS,
    [GlobalRoleScopeSection.GLOBAL_ROLES]: RouteName.GLOBAL_ROLES,
  },
});

/**
 * Allows access only when at least one users-related module is enabled and the users view is not restricted.
 * @param to
 * @param from
 * @param next
 */
export const usersTablePageNavigationGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const anyUsersModuleEnabled =
    !isModuleDisabled(SubscriptionModule.CLOUD_SECURITY) ||
    !isModuleDisabled(SubscriptionModule.EMAIL_SECURITY) ||
    !isModuleDisabled(SubscriptionModule.USER_DATA_GOVERNANCE) ||
    !isModuleDisabled(SubscriptionModule.SWG);
  return anyUsersModuleEnabled && !isAccessRestricted(RolePermissionsScope.VIEWS, "usersView")
    ? next()
    : next({ name: RouteName.DASHBOARD_PAGE });
};

/**
 * Allows access only when at least one device-related module is enabled.
 * @param to
 * @param from
 * @param next
 */
export const deviceTablePageNavigationGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const anyDeviceModuleEnabled = [
    SubscriptionModule.ENDPOINT_SECURITY,
    SubscriptionModule.EDR,
    SubscriptionModule.ENDPOINT_DATA_GOVERNANCE,
    SubscriptionModule.NETWORK,
    SubscriptionModule.SWG,
  ].some((module) => !isModuleDisabled(module));
  return anyDeviceModuleEnabled ? next() : next({ name: RouteName.DASHBOARD_PAGE });
};

/**
 * Restricts access to back office pages (coronet workspaces only) and optionally checks a nested global permission.
 * @param scopeName
 * @param key
 * @param nestedKey
 */
export const backOfficeGuard = <
  T extends keyof GlobalRolePermissions,
  K extends keyof GlobalRolePermissions[T],
  V extends (GlobalRolePermissions[T][K] extends NestedGlobalPermissions
    ? NonNullable<GlobalRolePermissions[T][K]["editAccessModePermission"]>
    : GlobalRolePermissions[T][K]),
>(
  scopeName?: T,
  key?: K,
  nestedKey?: keyof V
) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    // Backoffice page should be accessible only for coronet workspaces
    const backOfficeDisabled = isGlobalActionRestricted(
      GlobalRoleScopes.GLOBAL_SCOPE,
      GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
      "backOffice"
    );
    if (backOfficeDisabled) return next({ name: RouteName.DASHBOARD_PAGE });
    if (scopeName && key) {
      if (key === GlobalRoleScopeSection.SPECIAL_PERMISSIONS) {
        return isGlobalActionRestricted(scopeName, key as NonNullable<K>, nestedKey)
          ? next({ name: RouteName.DASHBOARD_PAGE })
          : next();
      }
      return isGlobalAccessRestricted(scopeName, key)
        ? next({ name: RouteName.DASHBOARD_PAGE })
        : next();
    }

    return next();
  };
};
